import { useState } from "react";
import { API } from "../../../services/api";
import {
    INVALID_INVITATION_EMAIL,
    invitationEmailUnavailableMessage,
    invitationFailureSentence,
    isValidInvitationEmail,
    sendEhpadInvitation,
    withoutTrailingDots,
} from "./ehpadInvitations";

const failureReason = err => withoutTrailingDots((err.response?.data?.message ?? "erreur inconnue").trim());

// Actions de raccordement proposées dans l'éditeur d'un EHPAD : annuler, renvoyer, créer ou réactiver.
export function useEhpadConnectionActions({
    ehpad, connected, pendingInvitation, deactivatedEmail, inviteEmail, setInviteEmail, onInvited, onConnectionChanged, setError, setNotice,
}) {
    const [resending, setResending] = useState(false);
    const [confirmCancelInvitation, setConfirmCancelInvitation] = useState(false);
    const [cancellingInvitation, setCancellingInvitation] = useState(false);
    const [reactivating, setReactivating] = useState(false);
    const [linking, setLinking] = useState(false);

    // Annuler le raccordement : sur un EHPAD raccordé, le lien EHPAD ↔ pharmacie est désactivé (rien n'est supprimé) ;
    // sinon l'invitation (en cours, bloquée ou expirée) est révoquée, le lien et le code ne servent plus.
    const handleCancelInvitation = async () => {
        setError(null);
        setNotice(null);
        setCancellingInvitation(true);
        try {
            if (connected) {
                await API.post(`/admin/ehpads/invitations/ehpad/${ehpad.id}/deactivate`);
                setNotice("Raccordement désactivé. Rien n'est supprimé : pour le réactiver, renvoyez une invitation.");
            } else {
                await API.delete(`/admin/ehpads/invitations/ehpad/${ehpad.id}`);
                setNotice(`Raccordement annulé : le lien et le code envoyés à ${pendingInvitation.email} ne fonctionnent plus.`);
            }
            onConnectionChanged();
        } catch (err) {
            setError(`Le raccordement n'a pas pu être ${connected ? "désactivé" : "annulé"} : ${failureReason(err)}.`);
        } finally {
            setCancellingInvitation(false);
            setConfirmCancelInvitation(false);
        }
    };

    // Contrat portail EHPAD : une invitation n'est jamais réactivée. « Renvoyer » (bloquée) et « Régénérer le code »
    // (en cours) émettent une nouvelle invitation au même email : nouveau lien, nouveau code, l'ancienne est révoquée.
    const handleResend = async () => {
        setError(null);
        setResending(true);
        try {
            onInvited(await sendEhpadInvitation(pendingInvitation.email, ehpad.id));
        } catch (err) {
            setError(invitationFailureSentence(err));
        } finally {
            setResending(false);
        }
    };

    // EHPAD dont le responsable a été créé par le portail : invitation (lien et code) envoyée à ce responsable ;
    // le portail vérifie le code sans créer de compte.
    const handleLink = async () => {
        setError(null);
        setNotice(null);
        setLinking(true);
        try {
            const { data } = await API.post(`/admin/ehpads/invitations/ehpad/${ehpad.id}/link`);
            onInvited(data);
        } catch (err) {
            setError(`Le raccordement n'a pas pu être créé : ${failureReason(err)}.`);
        } finally {
            setLinking(false);
        }
    };

    // Réactiver = renvoyer une invitation (nouveau lien, nouveau code) à l'email saisi, sinon à celui du dernier
    // raccordement. Même email : le portail vérifie le code sans créer de compte ; nouvel email : comme avant.
    const handleReactivate = async () => {
        setError(null);
        setNotice(null);
        const email = inviteEmail.trim() || deactivatedEmail;
        if (!isValidInvitationEmail(email)) {
            setError(INVALID_INVITATION_EMAIL);
            return;
        }
        setReactivating(true);
        try {
            const taken = await invitationEmailUnavailableMessage(email, ehpad.id);
            if (taken) {
                setError(taken);
                return;
            }
            onInvited(await sendEhpadInvitation(email, ehpad.id));
            setInviteEmail("");
        } catch (err) {
            setError(invitationFailureSentence(err));
        } finally {
            setReactivating(false);
        }
    };

    return {
        resending, reactivating, linking, cancellingInvitation, confirmCancelInvitation, setConfirmCancelInvitation,
        handleCancelInvitation, handleResend, handleLink, handleReactivate,
    };
}
